import { IsoDate, IsoDateTime, IsoDateTimeTz } from '../../../types/iso-date';
import { deconstructDate } from '../../helper/deconstruct-date.function';
import { shiftDateUtc } from '../../helper/shift-date-utc.function';
import { constructDate } from '../../helper/construct-date.function';

/**
 * Returns the start of the week for the given ISO input.
 * - For date-only (YYYY-MM-DD) -> returns 'YYYY-MM-DD'
 * - For date-time without tz -> returns 'YYYY-MM-DDT00:00:00.000'
 * - For date-time with tz -> returns 'YYYY-MM-DDT00:00:00.000Z|±HH:MM' preserving the original timezone
 *
 * Week start defaults to Monday; for locale 'en-US' it starts on Sunday.
 */
export function startOfWeek<T extends IsoDate | IsoDateTime | IsoDateTimeTz>(input: T, locale?: string): T {
  const weekStartsOn: 0 | 1 = (locale || '').toLowerCase() === 'en-us' ? 0 : 1; // 0=Sunday,1=Monday
  const deconstructed = deconstructDate(input);
  if (deconstructed._type !== 'date' && deconstructed._type !== 'dateTime' && deconstructed._type !== 'dateTimeTz') {
    throw new Error('startOfWeek: invalid ISO date/time string');
  }

  // Day of week for this calendar date (independent of timezone) using UTC helpers
  const dow = new Date(Date.UTC(deconstructed.year, deconstructed.month - 1, deconstructed.day)).getUTCDay(); // 0..6 (Sun..Sat)
  const delta = (dow - weekStartsOn + 7) % 7; // days since start of week
  const start = shiftDateUtc(deconstructed.year, deconstructed.month, deconstructed.day, -delta);

  deconstructed.day = start.day;
  deconstructed.month = start.month;
  deconstructed.year = start.year;

  if (deconstructed._type !== 'date') {
    deconstructed.hours = 0;
    deconstructed.minutes = 0;
    deconstructed.seconds = 0;
    deconstructed.milliseconds = 0;
  }

  return constructDate(deconstructed) as unknown as T;
}
